import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, signOut } from '@angular/fire/auth';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Inyecta el ID token de Firebase en toda peticion dirigida a `environment.urlApi`.
 *
 * CRITICO: el header va SIN el prefijo `Bearer`. El API usa APIKeyHeader y pasa
 * el valor crudo a `verify_id_token`; anteponer "Bearer " rompe el login con 401.
 *
 * Ante 401 / 403 cierra la sesion de Firebase y redirige a /login.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const auth = inject(Auth);
  const router = inject(Router);

  const isApiRequest = request.url.startsWith(environment.urlApi);
  if (!isApiRequest) return next(request);

  const token$ = from(
    auth.currentUser ? auth.currentUser.getIdToken() : Promise.resolve<string | null>(null),
  );

  return token$.pipe(
    switchMap((token) => {
      const authorized = token
        ? request.clone({ setHeaders: { Authorization: token } })
        : request;
      return next(authorized);
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        void signOut(auth).finally(() => {
          void router.navigate(['login'], { replaceUrl: true });
        });
      }
      return throwError(() => error);
    }),
  );
};
